import { Categories, getMainCategories, getSubcategories, getAllCategoriesFlat } from "../types/category";
import { getAccessToken } from './authService';

// Generate categories list from Category.ts
const generateCategoriesText = () => {
  const mainCategories = getMainCategories();
  let categoriesText = '';

  mainCategories.forEach(mainCategory => {
    // Add main category
    categoriesText += `${mainCategory.displayName} (${mainCategory.value})\n`;
    
    // Add subcategories
    const subCategories = getSubcategories(mainCategory.value);
    subCategories.forEach(subCategory => {
      categoriesText += `- ${subCategory.displayName} (${subCategory.value})\n`;
    });
    
    categoriesText += '\n';
  });

  return categoriesText;
};

const CATEGORIES_TEXT = generateCategoriesText();

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/**
 * Calls your secure backend proxy for AI chat
 */
export const getAIResponse = async (messages: Message[]): Promise<string> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/ai-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ messages })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} - ${errorData.message || 'Unknown error'}`);
    }

    const data = await response.json();
    return data.response;
  } catch (error) {
    console.error('Error calling AI service:', error);
    throw error;
  }
};

/**
 * Extracts JSON data from AI response
 */
export const extractWillDataFromResponse = (response: string): any => {
  try {
    // First try to find JSON block
    const jsonMatch = response.match(/```json\s*({[\s\S]*?})\s*```/);
    if (jsonMatch && jsonMatch[1]) {
      const jsonData = JSON.parse(jsonMatch[1]);
      
      // Process and standardize fields
      if (jsonData) {
        // Ensure all fields are properly formatted
        return {
          title: jsonData.title || '',
          description: jsonData.description || '',
          budget: jsonData.budget?.toString() || '',
          currency: jsonData.currency || 'ILS',
          category: jsonData.category || '',
          location: jsonData.location || '',
          
          // Time-related fields
          fulfillmentTimeType: jsonData.fulfillmentTimeType || 
                              (jsonData.when === 'asap' ? 'ASAP' : 
                               jsonData.when === 'this week' ? 'THIS_WEEK' : 
                               jsonData.date || jsonData.fulfillmentTime ? 'SPECIFIC_DATE' : ''),
          
          fulfillmentTime: jsonData.fulfillmentTime || '',
          fulfillmentDate: jsonData.fulfillmentDate || jsonData.date || '',
          
          // Optional fields
          recurrencePattern: jsonData.recurrencePattern || 
                           (jsonData.recurring === true ? 'DAILY' : 
                            jsonData.recurring ? jsonData.recurring.toUpperCase() : 'NONE'),
          
          budgetType: jsonData.budgetType || 
                     (jsonData.budget === 0 || jsonData.budget === '0' ? 'FREE' : 
                      jsonData.budget ? 'FIXED' : 
                      jsonData.openToOffers ? 'OPEN' : '')
        };
      }
    }
    
    // If no JSON block found, try parsing text for key information
    // This is a fallback if the AI doesn't return structured JSON
    const title = extractFieldFromText(response, 'title', 'name', 'subject');
    const description = extractFieldFromText(response, 'description', 'details', 'about');
    let budget = extractFieldFromText(response, 'budget', 'price', 'cost', 'amount');
    const category = extractFieldFromText(response, 'category', 'type');
    const location = extractFieldFromText(response, 'location', 'place', 'where');
    
    // Extract time-related information
    let fulfillmentTime = extractFieldFromText(response, 'when', 'date', 'time', 'deadline');
    let fulfillmentTimeType = '';
    let fulfillmentDate = '';
    let recurrencePattern = 'NONE';
    
    // Determine time type
    if (/asap|as soon as possible|immediately|now/i.test(response)) {
      fulfillmentTimeType = 'ASAP';
    } else if (/this week|within a week|in the coming week/i.test(response)) {
      fulfillmentTimeType = 'THIS_WEEK';
    } else if (fulfillmentTime || /specific date|on the|by the/i.test(response)) {
      fulfillmentTimeType = 'SPECIFIC_DATE';
      
      // Try to extract a date format
      const dateMatch = response.match(/\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{4}|\d{1,2}\.\d{1,2}\.\d{4}/);
      if (dateMatch) {
        fulfillmentDate = dateMatch[0];
      }
    }
    
    // Check for recurring pattern
    if (/daily|every day/i.test(response)) {
      recurrencePattern = 'DAILY';
    } else if (/weekly|every week/i.test(response)) {
      recurrencePattern = 'WEEKLY';
    } else if (/monthly|every month/i.test(response)) {
      recurrencePattern = 'MONTHLY';
    } else if (/yearly|annual|every year/i.test(response)) {
      recurrencePattern = 'YEARLY';
    } else if (/recurring|repeating|regular/i.test(response)) {
      recurrencePattern = 'CUSTOM';
    }
    
    // Determine budget type
    let budgetType = '';
    let currency = 'ILS';
    
    if (budget === '0' || /free|no cost/i.test(response)) {
      budgetType = 'FREE';
      budget = '0';
    } else if (budget || /fixed|exact/i.test(response)) {
      budgetType = 'FIXED';
      
      // Try to extract currency
      if (/\$|usd|dollar/i.test(response)) {
        currency = 'USD';
      } else if (/€|eur|euro/i.test(response)) {
        currency = 'EUR';
      } else if (/₪|ils|shekel|nis/i.test(response)) {
        currency = 'ILS';
      }
      
      // Clean the budget string
      if (budget) {
        budget = budget.replace(/[^\d.]/g, '');
      }
    } else if (/open to offers|flexible|negotiable/i.test(response)) {
      budgetType = 'OPEN';
    }
    
    return {
      title,
      description,
      budget,
      currency,
      category,
      location,
      fulfillmentTimeType,
      fulfillmentTime,
      fulfillmentDate,
      recurrencePattern,
      budgetType
    };
  } catch (error) {
    console.error('Error extracting will data from response:', error);
    return null;
  }
};

// Helper function to extract a field from text
function extractFieldFromText(text: string, ...fieldNames: string[]): string {
  for (const field of fieldNames) {
    const regex = new RegExp(`${field}\\s*[:=]\\s*([^\\n.]+)`, 'i');
    const match = text.match(regex);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  return '';
}

/**
 * Streams AI responses from your backend
 */
export const streamAIResponse = async (
  messages: Message[], 
  onChunk: (chunk: string) => void, 
  onComplete: (fullResponse: string) => void
): Promise<void> => {
  try {
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/ai-chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ messages })
    });

    if (!response.ok || !response.body) {
      throw new Error(`API error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let completeResponse = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      // Decode the chunk and add to buffer
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      
      // Process complete SSE events from the buffer
      const events = buffer.split('\n\n');
      buffer = events.pop() || ''; // Keep the last incomplete event in the buffer
      
      for (const event of events) {
        if (event.trim()) {
          onChunk(event);
          
          // Extract just the data content for the complete response
          const dataLines = event.split('\n')
            .filter(line => line.startsWith('data:'))
            .map(line => line.substring(5));
          
          completeResponse += dataLines.join('');
        }
      }
    }

    onComplete(completeResponse);
  } catch (error) {
    console.error('Error streaming AI response:', error);
    throw error;
  }
}; 